// TextTransformMenu.tsx
import { $patchStyleText } from '@lexical/selection';
import { $getNearestBlockElementAncestorOrThrow } from '@lexical/utils';
import { $getSelection, $isRangeSelection, $isTextNode, LexicalEditor } from 'lexical';
import React, { useCallback } from 'react';
import { ActionType } from '../components/ActionType';
import ResponsiveToolbar from '../components/ResponsiveToolbar';
import { Icons } from '../icons/Icons';
import { KeyValueMap } from '../KeyValueMap';
import { TEXT_TRANSFORM_OPTIONS } from './lexicalConstants';

type Props = {
    editor: LexicalEditor;
    disabled?: boolean;
    isSmall?: boolean;
};

export default function TextTransformMenu({ editor, disabled }: Props) {
    const applyTransform = useCallback(
        (key: string) => {
            if (!editor) return;

            editor.update(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return;

                const anchor = selection.anchor;
                const focus = selection.focus;
                const nodes = selection.getNodes();

                if (anchor.key === focus.key && anchor.offset === focus.offset) return;

                nodes.forEach((node, idx) => {
                    if (!$isTextNode(node)) return;

                    if (idx === 0 && anchor.offset !== 0) {
                        node = node.splitText(anchor.offset)[1] || node;
                    }
                    if (idx === nodes.length - 1 && focus.offset !== 0) {
                        node = node.splitText(focus.offset)[0] || node;
                    }

                    if (key === TEXT_TRANSFORM_OPTIONS.clear) {
                        try {
                            if (typeof node.setStyle === 'function') node.setStyle('');

                            if (typeof node.setFormat === 'function') node.setFormat(0);

                            try {
                                const ancestor = $getNearestBlockElementAncestorOrThrow(node);
                                if (ancestor?.setFormat) ancestor.setFormat('');
                            } catch {
                                /* ignore */
                            }
                        } catch {
                            /* ignore */
                        }
                        return;
                    }

                    if (
                        key === TEXT_TRANSFORM_OPTIONS.uppercase ||
                        key === TEXT_TRANSFORM_OPTIONS.lowercase ||
                        key === TEXT_TRANSFORM_OPTIONS.capitalize
                    ) {
                        const text = node.getTextContent();
                        let newText = text;
                        if (key === TEXT_TRANSFORM_OPTIONS.uppercase) newText = text.toUpperCase();
                        if (key === TEXT_TRANSFORM_OPTIONS.lowercase) newText = text.toLowerCase();
                        if (key === TEXT_TRANSFORM_OPTIONS.capitalize) {
                            newText = text.replace(
                                /\b\w+/g,
                                (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
                            );
                        }
                        if (newText !== text) {
                            node.setTextContent(newText);
                        }
                        return;
                    }

                    if (key === TEXT_TRANSFORM_OPTIONS.subscript || key === TEXT_TRANSFORM_OPTIONS.superscript) {
                        const verticalAlign =
                            key === TEXT_TRANSFORM_OPTIONS.subscript
                                ? TEXT_TRANSFORM_OPTIONS.sub
                                : TEXT_TRANSFORM_OPTIONS.super;
                        const fontSize = '0.8em';
                        const style = `vertical-align: ${verticalAlign}; font-size: ${fontSize};`;

                        if (typeof node.setStyle === 'function') {
                            node.setStyle(style);
                        } else {
                            $patchStyleText(selection, {
                                'vertical-align': verticalAlign,
                                'font-size': fontSize
                            });
                        }
                        return;
                    }
                });
            });
        },
        [editor]
    );

    const options: KeyValueMap<string, ActionType> = {
        uppercase: {
            label: 'common.uppercase',
            icon: Icons.Abc
        },
        lowercase: { label: 'common.lowercase', icon: Icons.LowercaseIcon },
        capitalize: { label: 'common.capitalize', icon: Icons.CapitalizeIcon },
        subscript: {
            label: 'common.subscript',
            icon: Icons.Subscript
        },
        superscript: { label: 'common.superscript', icon: Icons.Superscript },
        clear: { label: 'clear.formatting', icon: Icons.Delete }
    };

    const handleSelect = (selected: string | string[] | null) => {
        if (!selected) return;
        if (Array.isArray(selected)) {
            applyTransform(selected[0]);
        } else {
            applyTransform(selected);
        }
    };

    return (
        <ResponsiveToolbar
            disabled={disabled}
            isSmall
            selected={null}
            onSelect={handleSelect}
            options={options}
            multi={false}
            icon={Icons.AaIcon}
        />
    );
}
